"use client";

import { Button, Divider, Loader, TextInput } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { Icon } from "@repo/dashboard-core";
import Link from "next/link";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { useEffect, useState, useTransition } from "react";

import {
  requestMagicLink,
  setCustomerInfo,
  verifyToken,
} from "@repo/dashboard-core/actions";
import {
  magicLoginSchema,
  type MagicLoginFormValues,
} from "@repo/dashboard-core/schemas";

export default function LoginPage() {
  const t = useTranslations("auth.login");
  const tv = useTranslations("validation");
  const params = useParams<{ locale: string }>();
  const router = useRouter();
  const searchParams = useSearchParams();
  const token = searchParams.get("token");
  const errorParam = searchParams.get("error");

  const [isPending, startTransition] = useTransition();
  const [emailSent, setEmailSent] = useState(false);
  const [sentEmail, setSentEmail] = useState("");
  const [isVerifying, setIsVerifying] = useState(!!token);
  const [tokenError, setTokenError] = useState(errorParam === "invalid-token");

  const form = useForm<MagicLoginFormValues>({
    initialValues: { email: "" },
    validate: zodResolver(magicLoginSchema((key) => tv(key))),
  });

  // Auto-verify token if present in URL
  useEffect(() => {
    if (!token) return;

    const verify = async () => {
      setIsVerifying(true);
      try {
        const decoded = await verifyToken(token);
        if (decoded) {
          await setCustomerInfo(decoded);
          router.replace(`/${params.locale}`);
        } else {
          setIsVerifying(false);
          setTokenError(true);
        }
      } catch {
        setIsVerifying(false);
        setTokenError(true);
      }
    };

    verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSubmit = (values: MagicLoginFormValues) => {
    startTransition(async () => {
      const result = await requestMagicLink(values);

      if (!result.success) {
        notifications.show({
          title: t("error-title"),
          message: t("error-message"),
          color: "red",
        });
        return;
      }

      setSentEmail(values.email);
      setEmailSent(true);
    });
  };

  // Verifying token state
  if (isVerifying) {
    return (
      <div className="flex flex-col items-center justify-center space-y-4 py-8">
        <Loader size="lg" />
        <p className="text-base text-text-secondary">{t("verifying")}</p>
      </div>
    );
  }

  // Invalid token error state
  if (tokenError) {
    return (
      <div className="space-y-6 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
          <span className="text-red-500">
            <Icon name="alert" size={28} />
          </span>
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-text-primary">
            {t("error-title")}
          </h1>
          <p className="mt-2 text-base text-text-secondary">
            {t("error-invalid-token")}
          </p>
        </div>
        <Button
          variant="subtle"
          onClick={() => {
            setTokenError(false);
            router.replace(`/${params.locale}/login`);
          }}
          className="text-base font-medium text-primary"
        >
          {t("try-again")}
        </Button>
      </div>
    );
  }

  // Email sent success state
  if (emailSent) {
    return (
      <div className="space-y-6 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary-light">
          <span className="text-primary">
            <Icon name="email" size={28} />
          </span>
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-text-primary">
            {t("success-title")}
          </h1>
          <p className="mt-2 text-base text-text-secondary">
            {t("success-message", { email: sentEmail })}
          </p>
          <p className="mt-4 text-base font-medium text-text-primary">
            {t("success-cta")}
          </p>
        </div>
        <div className="space-y-2">
          <p className="text-base text-text-secondary">
            {t("success-alternative")}
          </p>
          <Link
            href={`/${params.locale}/passwordlogin`}
            className="inline-block text-base font-medium text-primary hover:underline"
          >
            {t("login-password")}
          </Link>
        </div>
      </div>
    );
  }

  // Default: email request form (magic link)
  return (
    <div className="space-y-8">
      <div className="text-center">
        <h1 className="text-2xl font-bold tracking-tight text-text-primary">
          {t("title")}
        </h1>
        <p className="mt-2 text-base text-text-secondary">{t("subtitle")}</p>
      </div>

      <form className="space-y-5" onSubmit={form.onSubmit(handleSubmit)}>
        <TextInput
          label={t("email")}
          placeholder="you@example.com"
          type="email"
          size="md"
          radius="lg"
          {...form.getInputProps("email")}
        />

        <Button
          type="submit"
          fullWidth
          size="md"
          radius="lg"
          loading={isPending}
        >
          {t("submit")}
        </Button>
      </form>

      <Divider label={t("or")} labelPosition="center" />

      <div className="text-center">
        <Link
          href={`/${params.locale}/passwordlogin`}
          className="text-base font-medium text-primary hover:underline"
        >
          {t("login-password")}
        </Link>
      </div>
    </div>
  );
}
